import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getText } from "../../../services/api/instances/assistantsApi/methods";
import { RootState } from "../../../services/redux/types";
import { TResponseApiRequestBody } from "../../../services/api/instances/responseApi/types/request";
import { generateId } from "../../../shared/utils/generateId";
import {
  deleteThreadWithProxy,
  getMessagesFromThreadWithProxy,
  postMessageToCompletion,
  postMessageToResponseWithProxy,
} from "../../../services/api/instances/responseApi/api";
import { OutputMessageType } from "../../../services/api/instances/responseApi/types/common";
import { SchemasValue } from "../../../services/api/instances/schemasApi/types";
import { editStorageObject } from "../../../app/state/storageObjectSlice/actions/actions";
import {
  mergedDialogMessages,
  transformMessagesObject,
} from "../../Dialog/helpers";
import {
  clearMessages,
  requestFinished,
  requestStarted,
  setSchemaValue,
  setTemporaryScheme,
} from "./slice";
import { extractXmlFromMarkdown } from "../helpers/extractXmlFromMarkdown";
import { getUpdatedSchemasValue } from "../helpers/getUpdatedSchemasValue";
import { TChatCompletionRequest } from "../../../services/api/instances/completionsApi/types";
import { INITIAL_SCHEMA_VALUE, SCHEMA_AGENT_REF } from "../constants";
import { createNotification } from "../../../app/state/notificationSlice/slice";
import { getError } from "../../../services/lib/getError";
import { TFileTypeConfig } from "../../Files/types";
import { getFileContentByHash } from "../../../services/api/instances/saveFileApi/methods";

export const getSchemaContentAction = createAsyncThunk(
  "schemas/getSchemaContent",
  async (
    {
      hash,
      fileConfig,
      fileName,
    }: { hash: string; fileConfig: TFileTypeConfig; fileName: string },
    { signal, rejectWithValue },
  ) => {
    try {
      const content = await getFileContentByHash(hash, fileConfig, fileName);
      if (!content) return;

      return content;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postSchemaMessageToChatCompletion = createAsyncThunk(
  "schemas/postSchemaMessageToChatCompletion",
  async (
    { fileData, id }: { fileData: string; id: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      dispatch(requestStarted({ objectId: id }));
      const { schemaValue } = (getState() as RootState).schemasState;
      if (!id || !schemaValue) return;

      const userMessage = [
        {
          role: "user",
          content: [
            {
              type: "image_url",
              image_url: {
                url: fileData,
              },
            },
          ],
        },
      ];
      const postObject: TChatCompletionRequest = {
        model: "openai/gpt-5.4-image-2",
        messages: [
          ...(schemaValue?.schemas_object?.messages ?? []),
          ...userMessage,
        ],
      };
      const systemResponse = await postMessageToCompletion(postObject);
      const systemMessage = systemResponse?.choices.at(-1);
      const fileUrl = systemMessage?.message.content as string;
      const { data } = (getState() as RootState).storageObjectState.schemas;
      const selectedObject = data.entities[id];
      const freshSchemaValue = selectedObject?.value as SchemasValue;
      if (!freshSchemaValue) return;
      const assistantMessage = {
        role: "assistant",
        content: `![image](${fileUrl})\n\n <file data-url="${fileUrl}" data-filename="${selectedObject?.friendly_name}.png">${selectedObject?.friendly_name}.png</file>`,
      };

      const updatedValue: SchemasValue = {
        ...freshSchemaValue,
        schemas_object: {
          ...freshSchemaValue?.schemas_object,
          messages: [
            ...(freshSchemaValue?.schemas_object?.messages ?? []),
            assistantMessage,
          ],
        },
      };

      const currentSelectedId = (getState() as RootState).storageObjectState
        .schemas.selectedId;
      if (currentSelectedId === id) {
        dispatch(setSchemaValue(updatedValue));
      }
      dispatch(
        editStorageObject({
          id,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "schemas",
        }),
      );
      dispatch(
        createNotification(
          "success",
          `Изображение в объекте ${selectedObject?.friendly_name} успешно сгенерировано`,
        ),
      );
      dispatch(requestFinished({ objectId: id }));
      return updatedValue;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const postSchemaMessageAction = createAsyncThunk(
  "schemas/postSchemaMessage",
  async (
    {
      message,
      threadRef,
      id,
    }: {
      message: string;
      threadRef: string | null;
      id: string;
    },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const { data } = (getState() as RootState).storageObjectState.schemas;
      const { schemaValue } = (getState() as RootState).schemasState;
      if (!id || !schemaValue) return;
      const selectedObject = data.entities[id];

      const threadId = threadRef ? threadRef : `thread_${generateId()}`;
      const postObject: TResponseApiRequestBody = {
        input: message,
        model: null,
        metadata: {
          assistant_ref: SCHEMA_AGENT_REF,
          thread_ref: threadId,
        },
      };

      dispatch(
        setTemporaryScheme({
          id,
          xml: schemaValue?.schemas_object?.schema_xml,
        }),
      );

      const systemResponse = await postMessageToResponseWithProxy(postObject);
      const systemMessage = systemResponse?.output.at(-1) as OutputMessageType;
      const systemMessageContent = systemMessage.content[0].text;
      const xml = extractXmlFromMarkdown(systemMessageContent);
      const updatedValue = getUpdatedSchemasValue(
        schemaValue,
        {
          schema_xml: xml ? xml : schemaValue.schemas_object.schema_xml,
        },
        {
          thread_ref: threadId,
        },
      );
      const currentSelectedId = (getState() as RootState).storageObjectState
        .schemas.selectedId;
      if (currentSelectedId === id) {
        dispatch(setSchemaValue(updatedValue));
      }
      if (currentSelectedId !== id) {
        dispatch(
          createNotification(
            "success",
            `Получен ответ в объекте схемы ${selectedObject?.friendly_name}`,
            3000,
          ),
        );
      }
      dispatch(
        editStorageObject({
          id,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "schemas",
        }),
      );
      return { systemMessageContent, xml, id };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const getSchemaMessagesAction = createAsyncThunk(
  "schemas/getSchemaMessages",
  async (
    { threadRef, id }: { threadRef: string; id: string },
    { getState, rejectWithValue },
  ) => {
    try {
      const { messages } = (getState() as RootState).schemasState;
      const newMessages = await getMessagesFromThreadWithProxy(threadRef);
      const receivedMessages = transformMessagesObject(newMessages);
      const finalMessages = mergedDialogMessages(
        messages[id] ? messages[id] : [],
        receivedMessages,
      );
      return finalMessages;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const deleteSchemaThreadAction = createAsyncThunk(
  "schemas/deleteSchemaThread",
  async (
    { threadRef, id }: { threadRef: string | null; id: string },
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const { schemaValue } = (getState() as RootState).schemasState;
      const { selectedId, data } = (getState() as RootState).storageObjectState
        .schemas;
      if (!selectedId || !schemaValue?.schemas_object) return;
      const selectedObject = data.entities[selectedId];
      if (!selectedObject) return;
      let updatedValue: SchemasValue = INITIAL_SCHEMA_VALUE;
      if (schemaValue?.metadata?.mode === "generate_schema" && threadRef) {
        dispatch(clearMessages(id));

        updatedValue = {
          ...schemaValue,
          metadata: {
            ...schemaValue.metadata,
            thread_ref: "",
          },
        };
        deleteThreadWithProxy(threadRef);
      } else if (schemaValue?.metadata?.mode === "generate_image") {
        updatedValue = {
          ...schemaValue,
          schemas_object: {
            ...schemaValue.schemas_object,
            messages: [],
          },
        };
      }
      dispatch(setSchemaValue(updatedValue));
      dispatch(
        editStorageObject({
          id: selectedId,
          name: selectedObject?.friendly_name,
          value: updatedValue,
          service: "schemas",
        }),
      );

      return;
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const uploadFileToSchemaDialogAction = createAsyncThunk(
  "schemas/uploadFileToSchemaDialog",
  async (file: File, { signal, rejectWithValue }) => {
    try {
      return await getText(file, signal);
    } catch (e) {
      if (
        signal.aborted ||
        axios.isCancel(e) ||
        (e instanceof Error && e.name === "AbortError")
      ) {
        return rejectWithValue("Aborted");
      }
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);
